import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { Feature, FeatureCollection, Polygon } from 'geojson'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'

export type BlacklistAreaProperties = {
  id?: number
  mainSign?: string
  speeds?: number[]
}

@Injectable({
  providedIn: 'root',
})
export class DataBlacklistAreaService {
  private blacklistAreas: FeatureCollection<Polygon, BlacklistAreaProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  private blacklistAreasSubject = new BehaviorSubject<
    FeatureCollection<Polygon, BlacklistAreaProperties>
  >({ type: 'FeatureCollection', features: [] })
  public blacklistAreas$ = this.blacklistAreasSubject.asObservable()
  public isDataInitialized = false

  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadRawEvents() {
    if (this.appState.isLoading) return

    this.cancelPreviousRequest$.next()
    this.isDataInitialized = true

    const featureCollection = await this.loadBlacklistAreas()
    this.blacklistAreas = {
      type: 'FeatureCollection',
      features: [...(featureCollection.features ?? [])],
    }
    this.blacklistAreasSubject.next({ ...this.blacklistAreas })

    return this
  }

  async loadBlacklistAreas(): Promise<FeatureCollection<Polygon, BlacklistAreaProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }

    const url = new URL(`${this.config.backend_url}vzo/blacklist-areas`)
    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Polygon, BlacklistAreaProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )

    return result ?? { type: 'FeatureCollection', features: [] }
  }

  async addBlacklistArea(
    polygon: Feature<Polygon>,
    mainSign: string,
    speeds?: number[],
  ): Promise<Feature<Polygon, BlacklistAreaProperties>> {
    const newArea: Feature<Polygon, BlacklistAreaProperties> = {
      ...polygon,
      properties: {
        ...(polygon.properties ?? {}),
        mainSign,
        speeds,
      },
    }

    await this.createOrUpdateBlacklistArea(newArea)
    await this.loadRawEvents()

    return newArea
  }

  async removeBlacklistArea(id: number): Promise<void> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      body: {},
    }
    const url = new URL(`${this.config.backend_url}vzo/blacklist-areas/${id}`)

    await firstValueFrom(
      this.http.delete(url.toString(), httpOptions).pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    await this.loadRawEvents()
  }

  async updateBlacklistArea(
    polygon: Feature<Polygon>,
    id: number,
    mainSign: string,
    speeds?: number[],
  ): Promise<void> {
    const updatedArea: Feature<Polygon, BlacklistAreaProperties> = {
      ...polygon,
      id,
      properties: {
        ...(polygon.properties ?? {}),
        id,
        mainSign,
        speeds,
      },
    }

    await this.createOrUpdateBlacklistArea(updatedArea)
    await this.loadRawEvents()
  }

  getAllBlacklistAreas(): FeatureCollection<Polygon, BlacklistAreaProperties> {
    return this.blacklistAreasSubject.value
  }

  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  private async createOrUpdateBlacklistArea(
    area: Feature<Polygon, BlacklistAreaProperties>,
  ): Promise<void> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(`${this.config.backend_url}vzo/blacklist-areas`)
    const { id: _ignoredPolygonId, ...polygonProperties } = area.properties ?? {}

    const payload = {
      type: 'Feature',
      geometry: area.geometry,
      properties: {
        ...polygonProperties,
        ...(area.properties?.id !== undefined ? { id: area.properties.id } : {}),
        mainSign: area.properties?.mainSign,
        speeds: area.properties?.speeds ?? null,
      },
    }

    await firstValueFrom(
      this.http
        .post(url.toString(), payload, httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
  }

  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
